/**
 * settingsSearch — finding a setting by name from ⌘K and the header search.
 *
 * Every tab and sub-tab of the Account and Location settings dialogs is listed
 * here with the words people actually type for it ("labor rate", "pay
 * period", "clock-in area", "declined"). Matching runs in the browser against
 * this list, so it can answer from the first character; accounts and
 * locations come from the server and keep the two-character minimum
 * (GLOBAL_SEARCH_MIN_CHARS).
 *
 * Only places the viewer can open are offered: sections and tabs come from
 * the settings registry (components/settings/registry), the same description
 * the settings page renders from, so who sees what cannot drift. This file
 * adds only the words people type for each place.
 */
import { isSystemAdmin } from '@turn-connect/hooks';
import { INVOICING_SECTION_LABELS, type InvoicingSectionId } from '../components/modals/invoicingSections';
import { sectionsFor, visibleSections } from '../components/settings/registry';
import type { SettingsKind, SettingsTarget } from './settingsDialogs';

export interface SettingsSection {
  scope: SettingsKind;
  tab: string;
  subTab?: string;
  /** What the row says: the sub-tab's label, or the tab's when it is the tab itself. */
  label: string;
  /** Other words for it, matched like the label. */
  synonyms: string[];
}

const TIME_SYNONYMS = {
  geofence: ['geofence', 'clock in area', 'gps', 'radius', 'fence', 'location check'],
  // Fraud checks and Auto logout share the Timesheet safeguards sub-tab.
  safeguards: [
    'fraud',
    'fraud detection',
    'fraud checks',
    'buddy punching',
    'suspicious punches',
    'unrecognized devices',
    'unusual hours',
    'auto logout',
    'auto clock out',
    'forgot to clock out',
  ],
};

const FINANCIAL_DEFAULTS = ['defaults', 'labor rate', 'auto approval amount', 'approval limit', 'pricing defaults', 'hourly rate'];
const DISCOUNTS = ['discounts', 'surcharges', 'fees', 'shop supplies', 'pricing rules'];
const OVERVIEW = ['overview', 'setup', 'to-do', 'to do', 'todo', 'checklist', 'summary'];
const CUSTOMIZATIONS = ['location overrides', 'customized', 'custom', 'overrides', 'differ'];

const sub = (scope: SettingsKind, tab: string, subTab: string, label: string, synonyms: string[] = []): SettingsSection => ({
  scope,
  tab,
  subTab,
  label,
  synonyms,
});
const invoicing = (tab: InvoicingSectionId, synonyms: string[] = []): SettingsSection => ({
  scope: 'account',
  tab: 'invoicing',
  subTab: tab,
  label: INVOICING_SECTION_LABELS[tab],
  synonyms,
});
const sectionLabel = (scope: SettingsKind, id: string): string => sectionsFor(scope).find((s) => s.id === id)?.label ?? id;
const top = (scope: SettingsKind, tab: string, synonyms: string[] = []): SettingsSection => ({
  scope,
  tab,
  label: sectionLabel(scope, tab),
  synonyms,
});

export const ACCOUNT_SETTINGS_SECTIONS: SettingsSection[] = [
  // Counts and setup to-dos.
  top('account', 'overview', OVERVIEW),
  top('account', 'details', [
    'details',
    'profile',
    'account name',
    'business name',
    'logo',
    'address',
    'time zone',
    'contact',
    'website',
    'tax id',
  ]),
  top('account', 'locations', ['rooftops', 'shops', 'stores']),
  sub('account', 'locations', 'list', 'Locations', ['add location', 'new location', 'delete location', 'restore location']),
  sub('account', 'locations', 'groups', 'Location groups', ['groups', 'districts']),
  top('account', 'people'),
  sub('account', 'people', 'users', 'Users', ['staff', 'employees', 'team', 'technicians']),
  sub('account', 'people', 'invitations', 'Invitations', ['invite', 'pending invitations', 'resend invitation']),
  sub('account', 'people', 'roles', 'Roles', ['permissions', 'access']),
  top('account', 'work-orders'),
  sub('account', 'work-orders', 'rules', 'Work order rules', ['required fields', 'share links']),
  sub('account', 'work-orders', 'checks', 'Completion checks & PIN', ['checks', 'completion checks', 'override pin', 'pin']),
  sub('account', 'work-orders', 'edit-lock', 'Lock after sync', ['edit lock', 'lock work orders after sync', 'locked work orders']),
  top('account', 'time-attendance', ['timesheets', 'time clock']),
  sub('account', 'time-attendance', 'geofence', 'Clock-in area', TIME_SYNONYMS.geofence),
  sub('account', 'time-attendance', 'safeguards', 'Timesheet safeguards', TIME_SYNONYMS.safeguards),
  sub('account', 'time-attendance', 'breaks', 'Breaks', ['lunch', 'break types']),
  sub('account', 'time-attendance', 'approvals', 'Pay periods & approvals', [
    'approvals',
    'pay period',
    'pay periods',
    'timesheet approval',
    'approve timesheets',
  ]),
  sub('account', 'time-attendance', 'payrules', 'Pay rules', ['overtime', 'double time', 'pay rates']),
  top('account', 'invoicing', ['invoices', 'money']),
  // AccountInvoicingTab's inner tabs; the rules three need invoice:rules:edit.
  invoicing('schedules', ['invoice schedules', 'billing schedule', 'when invoices go out', 'invoice rules']),
  invoicing('deliveryLists', ['invoice delivery', 'delivery lists', 'email invoices', 'invoice recipients']),
  invoicing('options', [
    'invoice options',
    'declined services',
    'price breakdown',
    'signature page',
    'invoice notes',
    'what the invoice shows',
  ]),
  invoicing('history', ['invoice history', 'sent invoices', 'invoice batches']),
  invoicing('sentEmails', [
    'emails',
    'sent emails',
    'who got the invoice',
    'invoice emails sent',
    'email history',
    'bounced',
    'blocked',
    'delivered',
    'delivery status',
    'delivery health',
    'failed deliveries',
    'bounced invoices',
  ]),
  invoicing('accounting', [
    'accounting',
    'quickbooks',
    'netsuite',
    'sync',
    'synced',
    "didn't sync",
    'sync failed',
    'paid invoices',
    'payments',
    'reconnect',
  ]),
  invoicing('aiCheckup', [
    'invoicing check-up',
    'invoicing advisor',
    'rule optimizer',
    'invoice checkup',
    'ai advisor',
    'invoice problems',
    'how a store is invoiced',
    'invoices never sent',
    'list with nobody',
  ]),
  top('account', 'pricing', ['money', 'prices']),
  sub('account', 'pricing', 'defaults', 'Labor rate & auto-approval', FINANCIAL_DEFAULTS),
  sub('account', 'pricing', 'discounts', 'Discounts & fees', DISCOUNTS),
  top('account', 'accounting', ['quickbooks', 'netsuite', 'accounting system', 'invoice sync']),
  sub('account', 'accounting', 'activityLog', 'Sync history', ['sync log', 'accounting sync history']),
  top('account', 'bill', ['billing', 'subscription', 'payment method', 'your bill']),
  top('account', 'pdr', ['paintless dent repair', 'dent']),
  // PDRAccountTab's sections; Overview and Estimate rules are for pdr:manage only.
  sub('account', 'pdr', 'overview', 'PDR overview', ['pdr setup', 'pdr pack', 'paintless dent repair']),
  sub('account', 'pdr', 'charts', 'Price charts', ['pdr charts', 'price book', 'dent chart', 'pdr prices']),
  sub('account', 'pdr', 'who-pays', 'Who pays', ['pdr payers', 'payers', 'insurance', 'pdr pricing by payer']),
  sub('account', 'pdr', 'randi', 'R&I prices', ['remove and install', 'r and i', 'pdr r&i']),
  sub('account', 'pdr', 'rules', 'Estimate rules', ['pdr rules', 'pdr estimate rules']),
  top('account', 'feeds', ['vehicle feeds', 'inventory feeds', 'dealer feeds', 'vin import']),
  top('account', 'partners', ['partner integrations', 'recon hub', 'dms']),
  sub('account', 'partners', 'connections', 'Partner connections', ['partner apps', 'connection codes']),
  sub('account', 'partners', 'mapping', 'Service matching', ['service mapping', 'service codes']),
  top('account', 'reporting', ['scheduled reports', 'reports']),
  // One list of every location that customized a setting, across every topic.
  top('account', 'customizations', CUSTOMIZATIONS),
];

export const LOCATION_SETTINGS_SECTIONS: SettingsSection[] = [
  // What the location customizes, and its setup to-dos.
  top('location', 'overview', OVERVIEW),
  top('location', 'details', [
    'details',
    'profile',
    'location name',
    'logo',
    'address',
    'time zone',
    'phone',
    'work order prefix',
    'location code',
    'production site',
    'location group',
  ]),
  top('location', 'people', ['users', 'staff', 'employees', 'team', 'technicians']),
  top('location', 'services'),
  sub('location', 'services', 'departments', 'Departments', ['workflow', 'stages', 'department order']),
  sub('location', 'services', 'services', 'Services', ['service catalog', 'work types', 'service prices']),
  sub('location', 'services', 'tags', 'Tags', ['labels', 'work order tags']),
  top('location', 'work-orders', ['restrictions', 'completion checks', 'override pin', 'pin']),
  top('location', 'time-attendance', ['timesheets', 'time clock']),
  sub('location', 'time-attendance', 'geofence', 'Clock-in area', TIME_SYNONYMS.geofence),
  sub('location', 'time-attendance', 'safeguards', 'Timesheet safeguards', TIME_SYNONYMS.safeguards),
  top('location', 'pricing', ['money', 'prices']),
  sub('location', 'pricing', 'defaults', 'Labor rate & auto-approval', FINANCIAL_DEFAULTS),
  sub('location', 'pricing', 'pricing', 'Discounts & fees', DISCOUNTS),
  top('location', 'accounting', ['accounting customer', 'subsidiary', 'quickbooks', 'netsuite', 'invoice sync']),
  top('location', 'feeds', ['vehicle feeds', 'inventory feeds', 'dealer feeds', 'vin import']),
  top('location', 'partners', ['partner connections', 'self-setup']),
];

/** The places of one scope this viewer can open: what the settings page shows them. */
export function availableSettingsSections(scope: SettingsKind, permissions: string[]): SettingsSection[] {
  const admin = isSystemAdmin(permissions);
  const canViewAccountReports =
    admin || permissions.includes('account:admin') || permissions.some((p) => p.startsWith('reports:') && p.endsWith(':account'));
  const visible = new Map(
    visibleSections(scope, { isEditMode: true, isSystemAdmin: admin, permissions, canViewAccountReports }).map((s) => [
      s.id,
      new Set(s.tabs.map((x) => x.id)),
    ])
  );
  const pool = scope === 'account' ? ACCOUNT_SETTINGS_SECTIONS : LOCATION_SETTINGS_SECTIONS;
  return pool.filter((s) => {
    const tabs = visible.get(s.tab);
    return !!tabs && (!s.subTab || tabs.has(s.subTab));
  });
}

const normalize = (text: string): string =>
  text
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const words = (text: string): string[] => normalize(text).split(' ').filter(Boolean);

/** The settings section a place sits in, as the page labels it. */
export function settingsSectionTabLabel(section: SettingsSection): string {
  return sectionLabel(section.scope, section.tab);
}

/**
 * Sections matching what was typed, best first. Every word typed must start
 * a word of the section's label, its tab's label or one of its synonyms
 * ("pay per" finds Approvals through "pay period"). A label that starts with
 * the whole term ranks first, then one of whose words does, then a synonym.
 */
export function matchSettingsSections(term: string, sections: SettingsSection[], limit = 5): SettingsSection[] {
  const q = normalize(term);
  if (!q) return [];
  const tokens = q.split(' ');
  const scored: Array<{ section: SettingsSection; score: number; index: number }> = [];
  sections.forEach((section, index) => {
    const label = normalize(section.label);
    const tabLabel = section.subTab ? normalize(settingsSectionTabLabel(section)) : '';
    const synonyms = section.synonyms.map(normalize);
    const vocabulary = [...words(section.label), ...(tabLabel ? tabLabel.split(' ') : []), ...synonyms.flatMap((s) => s.split(' '))];
    if (!tokens.every((t) => vocabulary.some((w) => w.startsWith(t)))) return;
    const score = label.startsWith(q)
      ? 0
      : label.split(' ').some((w) => w.startsWith(q))
        ? 1
        : synonyms.some((s) => s.startsWith(q) || s.includes(` ${q}`))
          ? 2
          : 3;
    scored.push({ section, score, index });
  });
  return scored
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .slice(0, limit)
    .map((s) => s.section);
}

/** Where a place is, for the result row: "Account settings", or "Account settings › Time & attendance". */
export function settingsSectionTrail(section: SettingsSection): string {
  const scope = section.scope === 'account' ? 'Account settings' : 'Location settings';
  return section.subTab ? `${scope} › ${settingsSectionTabLabel(section)}` : scope;
}

/** The dialog a section opens, in the account or location in scope. */
export function settingsSectionTarget(
  section: SettingsSection,
  scope: { accountId?: string | null; locationId?: string | null }
): SettingsTarget | null {
  if (section.scope === 'account') {
    return scope.accountId
      ? {
          kind: 'account',
          accountId: scope.accountId,
          tab: section.tab,
          subTab: section.subTab,
        }
      : null;
  }
  return scope.locationId
    ? { kind: 'location', locationId: scope.locationId, accountId: scope.accountId ?? undefined, tab: section.tab, subTab: section.subTab }
    : null;
}
